import { useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { BackLink } from '@/components/back-link';
import { api } from '@/lib/api';
import { TierBadge } from '@/components/tier-badge';
import { StatusChip, SuppressedChip } from '@/components/status-chip';
import { displayName, fmtDate, fmtAgo } from '@/lib/format';
import { ContactTabs, type Contact360 } from './contact-tabs';
import { ContactActions } from './contact-actions';

export async function contactLoader({ params }: LoaderFunctionArgs) {
  return api<Contact360>(`/contacts/${params.id}/360`);
}

export function ContactPage() {
  const data = useLoaderData<typeof contactLoader>();
  const { contact, subscription } = data;

  return (
    <div className="space-y-5">
      <BackLink to="/contacts">Contacts</BackLink>

      <header className="flex flex-wrap items-start justify-between gap-4 rounded-lg border bg-white p-5">
        <div>
          <h1 className="text-2xl">{displayName(contact)}</h1>
          <div className="text-sm text-muted-foreground">
            {contact.email}
            {contact.businessName ? ` · ${contact.businessName}` : ''}
            {contact.userFunction ? ` · ${contact.userFunction}` : ''}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <TierBadge tier={contact.tier} trialing={contact.subStatus === 'trialing'} />
            <StatusChip status={contact.subStatus} />
            <SuppressedChip reason={contact.suppressedReason} />
            {contact.organizationName && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs">org · {contact.organizationName}</span>
            )}
            {contact.source === 'import' && <span className="rounded bg-muted px-1.5 py-0.5 text-xs">lead</span>}
            {contact.tags.map((t) => (
              <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                #{t}
              </span>
            ))}
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Signed up</dt>
          <dd>{fmtDate(contact.ffCreatedAt)}</dd>
          <dt className="text-muted-foreground">Last activity</dt>
          <dd>{fmtAgo(contact.lastActivityAt)}</dd>
          <dt className="text-muted-foreground">Period ends</dt>
          <dd>
            {fmtDate(subscription?.currentPeriodEnd ?? contact.currentPeriodEnd)}
            {subscription?.cancelAtPeriodEnd ? ' (cancels)' : ''}
          </dd>
          <dt className="text-muted-foreground">Last synced</dt>
          <dd>{fmtAgo(contact.lastSyncedAt)}</dd>
        </dl>
      </header>

      <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
        <ContactTabs key={contact.id} data={data} />
        <ContactActions key={`${contact.id}-actions`} contact={contact} />
      </div>
    </div>
  );
}
